/**
 * Panel de administracion de portal tokens (Sprint 6 C).
 *
 * Se monta en CompleteSummary tras la firma de un assessment. Permite
 * al auditor:
 *   - Generar un enlace para el cliente (con opcion de envio por email)
 *   - Ver los tokens activos con acceso_count y last_accessed
 *   - Revocar tokens
 *
 * El token plano se muestra UNA SOLA VEZ inmediatamente despues de
 * crearlo, igual que los recovery codes de MFA. Si el auditor pierde
 * esta ventana puede generar uno nuevo y revocar el anterior.
 *
 * E3 H3.4 re-skin: Button E1, Input E1, Toast "Enlace copiado" 3s.
 * LÓGICA INTACTA: copyToClipboard, handleCreate, handleRevoke, refresh.
 */
import { useRef, useEffect, useState } from 'react';
import { Copy } from 'lucide-react';

import { ApiError } from '../api/client.js';
import { type PortalTokenCreated, type PortalTokenSummary, portalAdminApi } from '../api/portal.js';
import { Button } from './ui/Button.js';
import { Input } from './ui/Input.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastClose,
  useToast,
} from './ui/Toast.js';

interface Props {
  assessmentId: string;
  defaultClientName?: string;
}

export function PortalTokensPanel({ assessmentId, defaultClientName }: Props): JSX.Element {
  const [tokens, setTokens] = useState<PortalTokenSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [justCreated, setJustCreated] = useState<PortalTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toasts, toast, dismiss } = useToast();
  // Fallback ref para clipboard no disponible — WCAG fallback H3.4
  const urlInputRef = useRef<HTMLInputElement | null>(null);

  async function refresh(): Promise<void> {
    try {
      const r = await portalAdminApi.list(assessmentId);
      setTokens(r.tokens);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando tokens');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [assessmentId]);

  async function handleCreate(): Promise<void> {
    setError(null);
    setCreating(true);
    try {
      const input: { email_to?: string; client_name?: string } = {};
      if (emailTo.trim()) input.email_to = emailTo.trim();
      if (defaultClientName) input.client_name = defaultClientName;
      const created = await portalAdminApi.create(assessmentId, input);
      setJustCreated(created);
      setEmailTo('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error creando token');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(tokenId: string): Promise<void> {
    if (!confirm('¿Revocar este enlace? El cliente no podrá accederlo más.')) return;
    try {
      await portalAdminApi.revoke(assessmentId, tokenId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error revocando token');
    }
  }

  // LÓGICA INTACTA: navigator.clipboard.writeText
  // Añade: toast "Enlace copiado" 3s (H3.4) y fallback de selección si clipboard no disponible
  async function copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Enlace copiado', variant: 'success', duration: 3000 });
    } catch {
      // Fallback: mostrar input read-only con selección automática (WCAG H3.4 clipboard fallback)
      if (urlInputRef.current) {
        urlInputRef.current.value = text;
        urlInputRef.current.select();
      }
    }
  }

  return (
    /* ToastProvider + ToastViewport en la subtree del panel — un solo proveedor por subtree */
    <ToastProvider>
      <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="font-semibold text-cumple-primary-900">Compartir con cliente</h2>
          <span className="text-xs text-slate-500">
            {tokens.filter((t) => !t.revoked_at).length} activos
          </span>
        </header>

        {justCreated && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
            <p className="text-sm font-semibold text-amber-900">Enlace generado</p>
            <p className="text-xs text-amber-800">
              Cópialo ahora — por seguridad, no podrás volver a ver el token después de cerrar este
              mensaje.
            </p>
            <div className="rounded bg-white border border-amber-300 p-2 font-mono text-[10px] break-all text-amber-900">
              {justCreated.portal_url}
            </div>
            <div className="flex gap-2">
              {/* Button E1 secondary + Toast — WCAG 4.1.3 */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void copyToClipboard(justCreated.portal_url);
                }}
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
                Copiar enlace cliente
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setJustCreated(null)}>
                Cerrar
              </Button>
            </div>
            {/* Fallback clipboard: input sr-only seleccionable — WCAG H3.4 */}
            <input
              ref={urlInputRef}
              type="text"
              readOnly
              aria-label="URL del enlace copiable"
              defaultValue=""
              className="sr-only"
            />
          </div>
        )}

        <div className="space-y-2">
          {/* Input E1 — accesible con label, id, tipo email */}
          <Input
            id="email_to"
            type="email"
            label="Email del destinatario (opcional)"
            placeholder="cliente@empresa.com"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
          />
          <div className="flex gap-2 pt-1">
            {/* Button E1 primary — min-h-[44px] por CVA */}
            <Button
              variant="primary"
              size="md"
              disabled={creating}
              loading={creating}
              loadingText="Generando…"
              onClick={() => {
                void handleCreate();
              }}
            >
              {creating ? 'Generando…' : 'Generar enlace'}
            </Button>
          </div>
          <p className="text-[10px] text-slate-500">
            Si dejas el email vacío se genera el enlace sin envío automático para que lo compartas
            manualmente. Caduca en 30 días.
          </p>
        </div>

        {error && (
          <p role="alert" className="text-sm text-cumple-danger-500">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-xs text-slate-400">Cargando enlaces…</p>
        ) : tokens.length === 0 ? (
          <p className="text-xs text-slate-400">Aún no se ha generado ningún enlace.</p>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
            {tokens.map((t) => (
              <li key={t.id} className="px-3 py-2 text-xs">
                <div className="flex justify-between items-center gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-slate-700 truncate">{t.id.slice(0, 8)}</div>
                    <div className="text-[10px] text-slate-500">
                      creado {new Date(t.created_at).toLocaleDateString('es-DO')} · expira{' '}
                      {new Date(t.expires_at).toLocaleDateString('es-DO')}
                      {t.last_accessed_at &&
                        ` · último acceso ${new Date(t.last_accessed_at).toLocaleString('es-DO')}`}
                      {' · '}
                      {t.access_count} accesos
                    </div>
                  </div>
                  {t.revoked_at ? (
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500 shrink-0">
                      revocado
                    </span>
                  ) : (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        void handleRevoke(t.id);
                      }}
                      className="shrink-0 text-[10px]"
                    >
                      Revocar
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Toasts activos — WCAG 4.1.3: aria-live polite en el Viewport */}
      {toasts.map((t) => (
        <Toast
          key={t.id}
          open={t.open}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
          variant={t.variant}
          duration={t.duration ?? 3000}
        >
          <div className="flex-1">
            <ToastTitle>{t.title}</ToastTitle>
            {t.description && <p className="text-xs opacity-80 mt-0.5">{t.description}</p>}
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
